import { useEffect, useMemo, useState } from "react";
import { RowSelectionState, SortingState } from "@tanstack/react-table";
import { ArrowLeft, History, Loader2, Printer } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Modal } from "@/components/ui/customs/modal";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/ui/customs/data-table/data-table";
import {
  FilterSegmentedTabs,
  type SegmentedTabItem,
} from "@/components/shared/filterSegmentedTabs";
import {
  useGetAllExpeditionBo,
  useGetExpeditionCountWoRefetchBo,
  useGetPrintBatchBo,
  usePrintShippingLabels,
} from "@/api/expedition.api";
import { getFile } from "@/api/s3.api";
import { fetchFileBytes, mergeLabelsIntoPdf } from "@/lib/pdf.util";
import usePagination from "@/hooks/useSyncedPagination";
import { usePrintShippingLabelsTableColumns } from "./printShippingLabelsTableColumns";
import { usePrintBatchesTableColumns } from "./printBatchesTableColumns";
import { SinglePrintConfirmationModal } from "./singlePrintConfirmation.modal";
import { UploadFileBoModal } from "@/components/backOffice/expeditionForm/uploadFileModal";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import { GetAllExpeditionBoResponseDtoExpedition, PrintBatchBoDto } from "@/types/api/api-types";

type Props = {
  open: boolean;
  onClose: () => void;
};

type DispatchTab = "TO_BE_PRINTED_BATCH" | "TO_BE_PRINTED_SINGLE" | "PRINTED";
type ViewMode = "expeditions" | "batches";
type DownloadResult = "success" | "empty" | "error";

const openDocument = (url: string) => {
  const isIOSSafari =
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    /Safari/.test(navigator.userAgent) &&
    !/Chrome/.test(navigator.userAgent);

  if (isIOSSafari) {
    window.location.href = url;
    return;
  }

  const newWindow = window.open(url, "_blank");
  if (!newWindow) {
    window.location.href = url;
  }
};

const useDownloadShippingLabels = () => {
  const { t } = useTranslation("boExpeditions");
  const { mutateAsync: printLabels, isPending } = usePrintShippingLabels();

  const downloadLabels = async (expeditionIds: string[]): Promise<DownloadResult> => {
    if (expeditionIds.length === 0) return "empty";

    let documentsToPrint;
    try {
      ({ documentsToPrint } = await printLabels({ expeditionIds }));
    } catch {
      // handled by the mutation onError
      return "error";
    }

    if (documentsToPrint.length === 0) {
      toast.info(t("printShippingLabelsModal.noLabelsFound"));
      return "empty";
    }

    try {
      const pdfBuffers = await Promise.all(
        documentsToPrint.map(async (document) => fetchFileBytes(await getFile(document.path))),
      );

      const mergedPdf = await mergeLabelsIntoPdf(pdfBuffers);
      const blobUrl = URL.createObjectURL(
        new Blob([mergedPdf as BlobPart], { type: "application/pdf" }),
      );
      openDocument(blobUrl);

      toast.success(t("printShippingLabelsModal.printSuccess"));
      return "success";
    } catch (error) {
      console.error("Failed to merge shipping labels", error);
      toast.error(t("printShippingLabelsModal.mergeError"));
      return "error";
    }
  };

  return { downloadLabels, isPending };
};

type PrintExpeditionsProps = {
  open: boolean;
  onClose: () => void;
  onShowBatches: () => void;
};

const PrintExpeditions = ({ open, onClose, onShowBatches }: PrintExpeditionsProps) => {
  const { t } = useTranslation("boExpeditions");
  const [activeTab, setActiveTab] = useState<DispatchTab>("TO_BE_PRINTED_BATCH");
  const [sorting, setSorting] = useState<SortingState>([{ id: "finalizedAt", desc: true }]);
  const [pagination, setPagination] = usePagination("printShippingLabelsExpeditions");
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [confirmExpeditionId, setConfirmExpeditionId] = useState<string | null>(null);
  const [uploadExpedition, setUploadExpedition] =
    useState<GetAllExpeditionBoResponseDtoExpedition | null>(null);

  const { downloadLabels, isPending: isPrinting } = useDownloadShippingLabels();
  const isSingleTab = activeTab === "TO_BE_PRINTED_SINGLE";

  const { data: expeditions, isFetching } = useGetAllExpeditionBo({
    processStatus: ["VALIDATED"],
    dispatchStatus: activeTab,
    take: pagination.pageSize,
    skip: pagination.pageIndex * pagination.pageSize,
    sortBy: sorting[0]?.id,
    sortOrder: sorting[0]?.desc ? "DESC" : "ASC",
  });

  const { data: toBePrintedBatchCount } = useGetExpeditionCountWoRefetchBo({
    processStatus: ["VALIDATED"],
    dispatchStatus: "TO_BE_PRINTED_BATCH",
  });
  const { data: toBePrintedSingleCount } = useGetExpeditionCountWoRefetchBo({
    processStatus: ["VALIDATED"],
    dispatchStatus: "TO_BE_PRINTED_SINGLE",
  });
  const { data: printedCount } = useGetExpeditionCountWoRefetchBo({
    processStatus: ["VALIDATED"],
    dispatchStatus: "PRINTED",
  });

  const expeditionsData = useMemo(() => expeditions?.data ?? [], [expeditions]);
  const columns = usePrintShippingLabelsTableColumns({
    onUploadShippingLabel: setUploadExpedition,
  });
  const dispatchTabs = useMemo<SegmentedTabItem<DispatchTab>[]>(
    () => [
      { value: "TO_BE_PRINTED_BATCH", label: t("printShippingLabelsModal.tabs.batch") },
      { value: "TO_BE_PRINTED_SINGLE", label: t("printShippingLabelsModal.tabs.single") },
      { value: "PRINTED", label: t("printShippingLabelsModal.tabs.printed") },
    ],
    [t],
  );

  useEffect(() => {
    setRowSelection({});
    setConfirmExpeditionId(null);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [activeTab, setPagination]);

  useEffect(() => {
    setRowSelection({});
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    if (open) {
      setActiveTab("TO_BE_PRINTED_BATCH");
      setRowSelection({});
      setConfirmExpeditionId(null);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    } else {
      setConfirmExpeditionId(null);
      setUploadExpedition(null);
    }
  }, [open, setPagination]);

  const selectedIds = Object.keys(rowSelection)
    .filter((key) => rowSelection[key])
    .map((idx) => expeditionsData[parseInt(idx)]?.id)
    .filter((id): id is string => Boolean(id));

  const handlePrint = async () => {
    const result = await downloadLabels(selectedIds);
    if (result === "success" || result === "empty") {
      setRowSelection({});
    }
    return result;
  };

  const handlePrintClick = () => {
    if (isSingleTab) {
      const expeditionId = selectedIds[0];
      if (expeditionId) {
        setConfirmExpeditionId(expeditionId);
      }
      return;
    }
    void handlePrint();
  };

  const handleConfirmSinglePrint = async () => {
    const result = await handlePrint();
    if (result === "error") {
      throw new Error("print_failed");
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        dismissible
        size="xl"
        title={t("printShippingLabelsModal.title")}
        description={
          isSingleTab
            ? t("printShippingLabelsModal.singleDescription")
            : t("printShippingLabelsModal.batchDescription")
        }
        footer={
          <Button onClick={handlePrintClick} disabled={selectedIds.length === 0 || isPrinting}>
            {isPrinting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Printer className="h-4 w-4" />
            )}
            {isSingleTab
              ? t("printShippingLabelsModal.printSingle")
              : selectedIds.length > 0
                ? t("printShippingLabelsModal.printBatchWithCount", {
                    selectedCount: selectedIds.length,
                  })
                : t("printShippingLabelsModal.printBatch")}
          </Button>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <FilterSegmentedTabs
              tabs={dispatchTabs}
              value={activeTab}
              onChange={setActiveTab}
              counts={{
                TO_BE_PRINTED_BATCH: toBePrintedBatchCount ?? 0,
                TO_BE_PRINTED_SINGLE: toBePrintedSingleCount ?? 0,
                PRINTED: printedCount ?? 0,
              }}
              ariaLabel={t("printShippingLabelsModal.tabsAriaLabel")}
              className="w-fit flex-row"
            />
            <Button variant="outline" onClick={onShowBatches}>
              <History className="h-4 w-4" />
              {t("printShippingLabelsModal.batchHistory")}
            </Button>
          </div>

          <DataTable
            data={expeditionsData}
            columns={columns}
            pagination={pagination}
            setPagination={setPagination}
            sorting={sorting}
            setSorting={setSorting}
            rowCount={expeditions?.count || 0}
            autoResetPageIndex={false}
            enableRowSelection={(row) =>
              !!row.original.expeditionDocuments?.documents?.some(
                (document) => document.type === FileTypeEnum.EXPEDITION_SHIPPING_LABEL,
              )
            }
            enableMultiRowSelection={!isSingleTab}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            wrapperClassName={isFetching ? "opacity-60" : ""}
            headerClassName="bg-gray-300"
            headCellClassName="h-12 uppercase text-xs font-semibold tracking-wide text-gray-800 py-2 px-3"
            rowClassname={(row) =>
              row.expeditionDocuments?.documents?.some(
                (document) => document.type === FileTypeEnum.EXPEDITION_SHIPPING_LABEL,
              )
                ? ""
                : "bg-gray-100"
            }
          />
        </div>
      </Modal>

      <SinglePrintConfirmationModal
        open={!!confirmExpeditionId}
        expeditionId={confirmExpeditionId}
        onClose={() => setConfirmExpeditionId(null)}
        onConfirm={handleConfirmSinglePrint}
        isPrinting={isPrinting}
      />

      {uploadExpedition && (
        <UploadFileBoModal
          open
          onOpenChange={(value) => !value && setUploadExpedition(null)}
          expedition={uploadExpedition}
          expeditionId={uploadExpedition.id}
          expeditionDocumentsId={uploadExpedition.expeditionDocuments?.id}
          allowedFileTypes={[FileTypeEnum.EXPEDITION_SHIPPING_LABEL]}
          accept={["application/pdf"]}
          invalidateQueryKey={["expedition"]}
          defaultOptions={{ isPrivate: true, sendViaApi: false }}
          disabledOptions={{ isPrivate: true, sendViaApi: true }}
        />
      )}
    </>
  );
};

type PrintBatchesProps = {
  open: boolean;
  onClose: () => void;
  onShowExpeditions: () => void;
};

const PrintBatches = ({ open, onClose, onShowExpeditions }: PrintBatchesProps) => {
  const { t } = useTranslation("boExpeditions");
  const [pagination, setPagination] = usePagination("printShippingLabelsBatches");
  const [downloadingBatchId, setDownloadingBatchId] = useState<string | null>(null);

  const { downloadLabels } = useDownloadShippingLabels();

  const { data: printBatches, isFetching } = useGetPrintBatchBo(
    {
      take: pagination.pageSize,
      skip: pagination.pageIndex * pagination.pageSize,
    },
    open,
  );

  const printBatchesData = useMemo(() => printBatches?.data ?? [], [printBatches]);

  useEffect(() => {
    if (open) {
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setDownloadingBatchId(null);
    }
  }, [open, setPagination]);

  const handleDownloadBatch = async (batch: PrintBatchBoDto) => {
    setDownloadingBatchId(batch.id);
    try {
      await downloadLabels(batch.expeditionIds);
    } finally {
      setDownloadingBatchId(null);
    }
  };

  const columns = usePrintBatchesTableColumns({
    onDownload: handleDownloadBatch,
    downloadingBatchId,
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible
      size="xl"
      title={t("printShippingLabelsModal.title")}
      description={t("printShippingLabelsModal.batchesDescription")}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <Button variant="outline" onClick={onShowExpeditions}>
            <ArrowLeft className="h-4 w-4" />
            {t("printShippingLabelsModal.backToExpeditions")}
          </Button>
        </div>

        <DataTable
          data={printBatchesData}
          columns={columns}
          pagination={pagination}
          setPagination={setPagination}
          rowCount={printBatches?.count || 0}
          autoResetPageIndex={false}
          wrapperClassName={isFetching ? "opacity-60" : ""}
          headerClassName="bg-gray-300"
          headCellClassName="h-12 uppercase text-xs font-semibold tracking-wide text-gray-800 py-2 px-3"
        />
      </div>
    </Modal>
  );
};

export const PrintShippingLabelsModal = ({ open, onClose }: Props) => {
  const [viewMode, setViewMode] = useState<ViewMode>("expeditions");

  useEffect(() => {
    if (open) {
      setViewMode("expeditions");
    }
  }, [open]);

  if (viewMode === "batches") {
    return (
      <PrintBatches
        open={open}
        onClose={onClose}
        onShowExpeditions={() => setViewMode("expeditions")}
      />
    );
  }

  return (
    <PrintExpeditions open={open} onClose={onClose} onShowBatches={() => setViewMode("batches")} />
  );
};
